import { useEffect } from 'react';
import anime from 'animejs/lib/anime.es.js';

export function useHeroAnimations(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const ov = root.querySelector('.hero-cinematic-overlay');
      if (ov) ov.style.opacity = '0';
      return;
    }

    const mobile = window.innerWidth <= 900;
    const anims = [];

    // ── Entrance Timeline ──────────────────────────────────────────
    const tl = anime.timeline({ easing: 'easeOutExpo' });

    tl.add({
      targets: root.querySelector('.hero-cinematic-overlay'),
      opacity: [1, 0],
      duration: 800,
      easing: 'easeInOutQuad',
    });

    tl.add({
      targets: root.querySelector('.hero-badge'),
      opacity: [0, 1],
      translateY: [20, 0],
      duration: 700,
    }, '-=400');

    tl.add({
      targets: root.querySelectorAll('.score-chip'),
      opacity: [0, 1],
      translateY: [14, 0],
      scale: [0.92, 1],
      duration: 550,
      delay: anime.stagger(65),
    }, '-=400');

    tl.add({
      targets: root.querySelector('.hero-h1'),
      opacity: [0, 1],
      translateY: [24, 0],
      duration: 900,
    }, '-=250');

    tl.add({
      targets: root.querySelector('.hero-h1 em'),
      textShadow: [
        '0 0 0px rgba(255,203,5,0)',
        '0 0 10px rgba(255,203,5,0.2)',
      ],
      duration: 1000,
      easing: 'easeOutSine',
    }, '-=400');

    tl.add({
      targets: root.querySelector('.hero-sub'),
      opacity: [0, 1],
      translateY: [18, 0],
      duration: 800,
    }, '-=550');

    tl.add({
      targets: root.querySelectorAll('.hero-ctas > *'),
      opacity: [0, 1],
      translateY: [16, 0],
      duration: 650,
      delay: anime.stagger(90),
    }, '-=450');

    tl.add({
      targets: root.querySelectorAll('.btn-link-hero, .hero-fine'),
      opacity: [0, 1],
      translateY: [10, 0],
      duration: 500,
      delay: anime.stagger(70),
    }, '-=350');

    tl.add({
      targets: root.querySelector('.hero-insight'),
      opacity: [0, 1],
      translateX: [-18, 0],
      duration: 700,
    }, '-=250');

    tl.add({
      targets: root.querySelector('.planes-card'),
      opacity: [0, 1],
      translateY: [mobile ? 20 : 35, 0],
      scale: [0.97, 1],
      duration: 1000,
      easing: 'easeOutCubic',
    }, '-=700');

    anims.push(tl);

    // ── Looping Animations ─────────────────────────────────────────

    anims.push(anime({
      targets: root.querySelector('.btn-primary-hero'),
      boxShadow: [
        { value: '0 0 0 0 rgba(0,85,255,0)', duration: 0 },
        { value: '0 0 20px 3px rgba(0,85,255,0.2)', duration: 1400 },
        { value: '0 0 0 0 rgba(0,85,255,0)', duration: 1400 },
      ],
      easing: 'easeInOutSine',
      loop: true,
      delay: 3000,
    }));

    if (!mobile) {
      anims.push(anime({
        targets: root.querySelector('.planes-card'),
        translateY: [-3, 3],
        duration: 5000,
        loop: true,
        direction: 'alternate',
        easing: 'easeInOutSine',
        delay: 4000,
      }));
    }

    anims.push(anime({
      targets: root.querySelector('.hero-glow'),
      opacity: [0.6, 1],
      duration: 7000,
      loop: true,
      direction: 'alternate',
      easing: 'easeInOutSine',
    }));

    anims.push(anime({
      targets: root.querySelector('.hero-h1 em'),
      textShadow: [
        '0 0 8px rgba(255,203,5,0.15)',
        '0 0 18px rgba(255,203,5,0.32)',
        '0 0 8px rgba(255,203,5,0.15)',
      ],
      duration: 3500,
      loop: true,
      easing: 'easeInOutSine',
      delay: 2500,
    }));

    return () => {
      anims.forEach(a => a.pause());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
